import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useCashbookStatus } from "../../../../helpers/cashbookStatus";
import { clearMissing, getCashInbox, listCashHolders, verifyEntries } from "../../../../services/cashBookApi";
import { T, useKT } from "../../i18n/kt";
import { kp } from "../../karkhanaPaths";
import { newRequestId } from "../../rounds/entryQueue";
import { fmtDay, rupees } from "../../rates/rateModel";
import PageHeader from "../../ui/PageHeader";
import { readRole } from "../CashbookGuard";
import { fmtTime, pageList } from "../money";
import type { CashInbox, EntryView, Holder, InboxDay, InboxMissing } from "../types";
import { toCheckLine } from "./CashToCheckAlert";
import ChangeDecision, { changeTitle } from "./ChangeDecision";
import { isOwnerRole, ownerRefusal } from "./ownerText";
import { GROUP_MAX } from "./sheetCheckModel";
import "../cashbook.scss";
import "./owner.scss";

export type OwnerTab = "inbox" | "history" | "supervisors" | "heads";

/** To check · Checked history · Supervisors · Spend types (11.6 L1). */
export const OwnerTabs = ({ on }: { on: OwnerTab }) => {
  const { t } = useKT();
  const tabs: [OwnerTab, string, string][] = [
    ["inbox", "To check", kp.cashBook()],
    ["history", "Checked history", kp.cashHistory()],
    ["supervisors", "Supervisors", kp.cashBook({ tab: "supervisors" })],
    ["heads", "Spend types", kp.cashHeads()],
  ];
  return (
    <nav className="cbo-tabs" aria-label={t("Cash book")}>
      {tabs.map(([key, label, to]) => (
        <Link key={key} to={to} className={on === key ? "is-on" : ""} aria-current={on === key ? "page" : undefined}>
          {t(label)}
        </Link>
      ))}
    </nav>
  );
};

/** "3 sheets · 2 photos missing · 1 change to decide · 1 handover · 4 other entries" — parts that are not 0. */
export const summaryLine = (s: CashInbox["summary"], t: T): string =>
  [toCheckLine(s, t), s.otherEntries ? t(s.otherEntries === 1 ? "1 other entry" : "{{n}} other entries", { n: s.otherEntries }) : ""].filter(Boolean).join(" · ");

/** Days oldest first, whatever order the server sent (D36). */
export const oldestFirst = (days: InboxDay[]): InboxDay[] => [...days].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

export const missingText = (m: InboxMissing, date: string, t: T): string =>
  m.kind === "HAND_PAY"
    ? t(m.entries === 1 ? "{{name}} paid {{amount}} by hand on {{date}} (1 entry), no sheet photo." : "{{name}} paid {{amount}} by hand on {{date}} ({{n}} entries), no sheet photo.", {
        name: m.holder.name,
        amount: rupees(m.amount ?? null),
        date: fmtDay(date),
        n: m.entries ?? 0,
      })
    : m.kind === "PAY_SHEET"
    ? m.pagesMissing.length && m.sheetNo
      ? t("Missing: {{name}}'s pay sheet {{sheetNo}} (pages {{pages}}).", { name: m.holder.name, sheetNo: m.sheetNo, pages: pageList(m.pagesMissing) })
      : t("Missing: {{name}}'s pay sheet {{sheetNo}}.", { name: m.holder.name, sheetNo: m.sheetNo || fmtDay(date) })
    : t("Missing: {{name}}'s spends page for {{date}}.", { name: m.holder.name, date: fmtDay(date) });

const useWide = () => {
  const q = "(min-width: 1200px)";
  const [wide, setWide] = useState(() => typeof window !== "undefined" && !!window.matchMedia && window.matchMedia(q).matches);
  useEffect(() => {
    if (!window.matchMedia) return;
    const m = window.matchMedia(q);
    const on = () => setWide(m.matches);
    m.addEventListener?.("change", on);
    return () => m.removeEventListener?.("change", on);
  }, []);
  return wide;
};

/** Laptop: the number with a Copy button. Phone and iPad: a call link (D36). */
const Phone = ({ name, phone }: { name: string; phone: string | null }) => {
  const { t } = useKT();
  const wide = useWide();
  const [copied, setCopied] = useState(false);
  if (!phone) return null;
  if (!wide)
    return (
      <a className="kh-btn" href={`tel:${phone.replace(/[^\d+]/g, "")}`}>
        {t("Call {{name}}", { name })}
      </a>
    );
  return (
    <span className="cbo-card__btns">
      <span>{phone}</span>
      <button
        type="button"
        className="kh-btn"
        onClick={() => {
          navigator.clipboard?.writeText(phone).then(
            () => setCopied(true),
            () => setCopied(false)
          );
        }}
      >
        {copied ? t("Copied") : t("Copy")}
      </button>
    </span>
  );
};

const entryLine = (e: EntryView, t: T) => {
  const what =
    e.kind === "EXPENSE"
      ? [e.headName, e.paidTo].filter(Boolean).join(" · ") || t("Spend")
      : e.kind === "HANDOVER"
        ? t("Cash handed to {{name}}", { name: e.holder?.name || "" })
        : e.kind === "RETURN"
          ? t("{{name}} gave cash back", { name: e.holder?.name || "" })
          : e.kind === "TRANSFER"
            ? t("{{from}} → {{to}}", { from: e.transfer?.from?.name || "", to: e.transfer?.to?.name || "" })
            : [e.kind === "ADVANCE" ? t("Advance") : t("Pay"), e.personName].filter(Boolean).join(" · ");
  return `${what} · ${rupees(e.amount)}`;
};

/** The checker's own entry (recorded it, or their own pay): never in a group; the owner may override one. */
const isOwnEntry = (e: EntryView) => e.canVerify === "RECORDED_BY_YOU" || e.canVerify === "YOUR_OWN_PAY";

/**
 * "Other entries (n)": tick up to 10 and verify them in one tap (/verify, 7.6). The owner's own entries
 * (a handover he gave, pay he recorded) are verified one at a time with "Verify anyway" and a reason,
 * marked self-verified (D14), so a single-owner studio is never left with them outstanding.
 */
/**
 * A day of hand payments has no sheet to photograph (owner decision 2026-11-02): the owner or a manager
 * marks why, once. The reason is kept with who marked it; it is never edited.
 */
export const MarkReason = ({ m, date, onDone }: { m: InboxMissing; date: string; onDone: () => void }) => {
  const { t } = useKT();
  const [open, setOpen] = useState(false);
  const [why, setWhy] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) {
    return (
      <button type="button" className="kh-btn is-primary" onClick={() => setOpen(true)}>
        {t("Mark reason")}
      </button>
    );
  }
  const save = async () => {
    if (!why.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const res = await clearMissing({ holderUserId: m.holder.userId, date: m.date || date, kind: "HAND_PAY", reason: why.trim() });
      if (!res?.success) {
        setError(ownerRefusal(res, t));
        return;
      }
      onDone();
    } catch {
      setError(t("Not saved. Check the network and retry."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="cbo-decide">
      <label className="cb-field">
        <span className="cb-field__label">{t("Why is there no photo?")}</span>
        <textarea rows={2} maxLength={500} value={why} onChange={(e) => setWhy(e.target.value)} placeholder={t("e.g. Printer broken, paid from the register")} />
      </label>
      <div className="cbo-decide__btns">
        <button type="button" className="kh-btn is-primary" disabled={!why.trim() || busy} onClick={save}>
          {busy ? t("Saving…") : t("Save reason")}
        </button>
        <button type="button" className="kh-btn" disabled={busy} onClick={() => setOpen(false)}>
          {t("Cancel")}
        </button>
      </div>
      <p className="cb-muted">{t("The reason is kept with your name and can't be changed.")}</p>
      {error ? (
        <p className="cbo-row__err" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
};

const OtherEntries = ({ entries, onDone, owner }: { entries: EntryView[]; onDone: () => void; owner: boolean }) => {
  const { t } = useKT();
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [overriding, setOverriding] = useState<EntryView | null>(null);
  const [reason, setReason] = useState("");
  const [shownAt] = useState(Date.now);
  const key = (e: EntryView) => `${e.recordType}:${e.id}`;
  const ids = Object.keys(picked).filter((k) => picked[k]);
  const item = (e: EntryView) => ({ recordType: e.recordType, id: e.id, version: e.version, photoSha256: null, onScreenMs: Date.now() - shownAt });
  const verify = async (one?: EntryView, why?: string) => {
    const items = one ? [item(one)] : entries.filter((e) => picked[key(e)]).slice(0, GROUP_MAX).map(item);
    if (!items.length) return;
    setBusy(true);
    setError(null);
    try {
      const res = await verifyEntries({ batchId: newRequestId(), items, override: one && why ? { reason: why } : null });
      if (!res?.success || !res.data) {
        setError(ownerRefusal(res, t));
        return;
      }
      const failed = res.data.results.filter((r) => !r.ok);
      if (failed.length) setError(failed.map((f) => ownerRefusal(f, t)).join(" "));
      if (!one) setPicked({});
      setOverriding(null);
      onDone();
    } catch {
      setError(t("Not saved. Check the network and retry."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <li className="cbo-card">
      <span className="cbo-card__main">
        <button type="button" className="cb-link" aria-expanded={open} onClick={() => setOpen(!open)}>
          {t("Other entries ({{n}})", { n: entries.length })}
        </button>
        {open ? (
          <>
            <ul className="cbo-verify-list">
              {entries.map((e) => {
                const own = e.canVerify !== "OK";
                return (
                  <li key={key(e)}>
                    <label>
                      <input
                        type="checkbox"
                        disabled={own || (!picked[key(e)] && ids.length >= GROUP_MAX)}
                        checked={!!picked[key(e)]}
                        onChange={(ev) => setPicked((p) => ({ ...p, [key(e)]: ev.target.checked }))}
                      />
                      <span>
                        {entryLine(e, t)}
                        {e.section40A ? (
                          <span className="cbo-card__sub">
                            {t("{{amount}} cash to one payee today. Over ₹10,000 in cash may not count as a tax expense.", { amount: rupees(e.section40A.dayTotal) })}
                          </span>
                        ) : null}
                      </span>
                    </label>
                    {e.personId ? (
                      <Link to={kp.cashWorker(e.personId)} className="cb-link">
                        {t("Worker payments")}
                      </Link>
                    ) : null}
                    {own ? <span className="cbo-chip is-own">{t(e.canVerify === "YOUR_OWN_PAY" ? "Your own pay" : "You recorded this")}</span> : null}
                    {owner && isOwnEntry(e) ? (
                      <button
                        type="button"
                        className="kh-btn"
                        disabled={busy}
                        onClick={() => {
                          setReason("");
                          setOverriding(e);
                        }}
                      >
                        {t("Verify anyway")}
                      </button>
                    ) : null}
                  </li>
                );
              })}
            </ul>
            <button type="button" className="kh-btn is-primary" disabled={!ids.length || busy} onClick={() => verify()}>
              {busy ? t("Saving…") : t("Verify these {{n}}", { n: ids.length })}
            </button>
            {error ? (
              <p className="cbo-row__err" role="alert">
                {error}
              </p>
            ) : null}
          </>
        ) : null}
      </span>
      {overriding ? (
        <div className="cbo-dialog" role="dialog" aria-modal="true" aria-labelledby="cbo-entry-dialog-title">
          <div className="cbo-dialog__box">
            <h3 id="cbo-entry-dialog-title">{t('Verify your own entry? It stays marked "self-verified" in the exceptions report.')}</h3>
            <p className="cb-muted">{entryLine(overriding, t)}</p>
            <label className="cb-field">
              <span className="cb-field__label">{t("Why?")}</span>
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} autoFocus />
            </label>
            <div className="cbo-dialog__btns">
              <button type="button" className="kh-btn" onClick={() => setOverriding(null)}>
                {t("Leave for someone else")}
              </button>
              <button type="button" className="kh-btn is-primary" disabled={!reason.trim() || busy} onClick={() => verify(overriding, reason.trim())}>
                {t("Verify anyway")}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </li>
  );
};

const Supervisors = () => {
  const { t } = useKT();
  const [holders, setHolders] = useState<Holder[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await listCashHolders();
      if (res?.success && res.data) setHolders(res.data.holders || []);
      else setError(ownerRefusal(res, t));
    } catch {
      setError(t("Couldn't load. Check the network."));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
  }, [load]);
  if (error && !holders)
    return (
      <div className="cb-error" role="alert">
        <span>{error}</span>
        <button type="button" className="kh-btn" onClick={load}>
          {t("Retry")}
        </button>
      </div>
    );
  if (!holders) return <div className="cb-skeleton" role="status" aria-label={t("Loading…")} />;
  if (!holders.length) return <p className="cb-muted">{t("No supervisors yet. Add them under Supervisors in Karkhana settings.")}</p>;
  return (
    <ul className="cbo-cards">
      {holders.map((h) => (
        <li key={h.userId} className="cbo-card">
          <Link to={kp.cashHolder(h.userId)} className="cbo-card__main">
            <strong>{h.name}</strong>
            {h.missing ? <span className="cbo-card__sub">{t(h.missing === 1 ? "1 photo missing" : "{{n}} photos missing", { n: h.missing })}</span> : null}
            <span className="cbo-card__sub">
              {t("Cash in hand")} <strong>{rupees(h.inHand ?? 0)}</strong>
            </span>
          </Link>
          <span className="cbo-card__btns">
            <Link to={kp.cashHolder(h.userId, { act: "handover" })} className="kh-btn">
              {t("Hand over cash")}
            </Link>
          </span>
        </li>
      ))}
    </ul>
  );
};

const Inbox = () => {
  const { t } = useKT();
  const status = useCashbookStatus();
  const owner = isOwnerRole(readRole());
  const [data, setData] = useState<CashInbox | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deciding, setDeciding] = useState<number | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await getCashInbox(14);
      if (res?.success && res.data) setData(res.data);
      else setError(ownerRefusal(res, t));
    } catch {
      setError(t("Couldn't load. Check the network."));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
  }, [load]);

  const reload = () => {
    load();
    status.refresh();
  };

  if (error && !data)
    return (
      <div className="cb-error" role="alert">
        <span>{error}</span>
        <button type="button" className="kh-btn" onClick={load}>
          {t("Retry")}
        </button>
      </div>
    );
  if (!data)
    return (
      <div role="status" aria-label={t("Loading…")}>
        <div className="cb-skeleton" />
        <div className="cb-skeleton" />
        <div className="cb-skeleton" />
      </div>
    );

  const days = oldestFirst(data.days);
  const nothing = !days.length && !data.changes.length && !data.transfers.length;
  const summary = summaryLine(data.summary, t);
  const speed = owner ? data.speed || [] : [];

  return (
    <>
      {summary ? (
        <p className="cbo-summary" aria-live="polite">
          {summary}
        </p>
      ) : null}
      {nothing ? <div className="cb-card is-ok">{t("Nothing to check. Every sheet up to yesterday is checked.")}</div> : null}

      {data.changes.length ? (
        <section className="cbo-day">
          <h3 className="cbo-day__head">{t("Changes to decide")}</h3>
          <ul className="cbo-cards">
            {data.changes.map((c) => (
              <li key={c.proposalSeq} className="cbo-card">
                <span className="cbo-card__main">
                  {changeTitle(c, t)}
                  <span className="cbo-card__sub">{fmtDay(c.requestedAt.slice(0, 10))}</span>
                  {deciding === c.proposalSeq && !c.person ? <ChangeDecision change={c} onDecided={() => status.refresh()} /> : null}
                </span>
                {c.person && c.recordType !== "CASH_ENTRY" ? (
                  <Link to={kp.cashWorker(c.person.id)} className="kh-btn is-primary">
                    {t("Decide")}
                  </Link>
                ) : deciding !== c.proposalSeq ? (
                  <button type="button" className="kh-btn is-primary" onClick={() => setDeciding(c.proposalSeq)}>
                    {t("Decide")}
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {data.transfers.length ? (
        <section className="cbo-day">
          <h3 className="cbo-day__head">{t("Handovers")}</h3>
          <ul className="cbo-cards">
            {data.transfers.map(({ transfer: x, why }) => (
              <li key={x.id}>
                <Link to={kp.cashTransfer(x.id)} className="cbo-card is-warn">
                  <span className="cbo-card__main">
                    {why === "STUCK"
                      ? t("{{amount}} from {{from}} to {{to}} has been in transit for a day.", { amount: rupees(x.amount), from: x.from.name, to: x.to.name })
                      : why === "DISPUTED"
                        ? t("{{from}} says they gave {{to}} {{amount}} (handover disputed).", { amount: rupees(x.amount), from: x.from.name, to: x.to.name })
                        : t("{{to}} said no to {{amount}} from {{from}}.", { amount: rupees(x.amount), from: x.from.name, to: x.to.name })}
                  </span>
                  <span className="kh-btn">{t("Open")}</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {days.map((d) => (
        <section key={d.date} className="cbo-day">
          <h3 className="cbo-day__head">
            {fmtDay(d.date)}
            {d.ageDays > 0 ? <span className={`cbo-chip ${d.ageDays >= 2 ? "is-bad" : "is-doubt"}`}>{t(d.ageDays === 1 ? "1 day" : "{{n}} days", { n: d.ageDays })}</span> : null}
          </h3>
          <ul className="cbo-cards">
            {d.prints.map((p) => (
              <li key={p.printId}>
                <Link to={kp.cashCheck(p.printId)} className="cbo-card">
                  <span className="cbo-card__main">
                    <strong>{p.sheetNo}</strong>
                    {p.holder ? ` · ${p.holder.name}` : ""} · {t("{{n}} rows", { n: p.rows })} · {t("sent {{time}}", { time: fmtTime(p.sentAt) })}
                    {p.totalsDiffer ? (
                      <>
                        {" "}
                        <span className="cbo-chip is-doubt">{t("Totals differ")}</span>
                      </>
                    ) : null}
                    <span className="cbo-card__sub">
                      {[
                        p.verified ? t("{{n}} of {{total}} verified", { n: p.verified, total: p.rows }) : "",
                        p.pagesSent.length < p.pages ? t("pages {{pages}} not sent", { pages: pageList(Array.from({ length: p.pages }, (_, i) => i + 1).filter((x) => !p.pagesSent.includes(x))) }) : "",
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </span>
                  <span className="kh-btn is-primary">{t("Check")}</span>
                </Link>
              </li>
            ))}
            {d.spends.map((s) => (
              <li key={s.photoId}>
                <Link to={kp.cashSpendsCheck(s.photoId)} className="cbo-card">
                  <span className="cbo-card__main">
                    {t("Spends page · {{name}} · {{n}} lines", { name: s.holder.name, n: s.entries })}
                    <span className="cbo-card__sub">{t("sent {{time}}", { time: fmtTime(s.sentAt) })}</span>
                  </span>
                  <span className="kh-btn is-primary">{t("Check")}</span>
                </Link>
              </li>
            ))}
            {d.missing.map((m, i) => (
              <li key={`${m.holder.userId}-${m.kind}-${i}`} className="cbo-card is-warn">
                <span className="cbo-card__main">{missingText(m, d.date, t)}</span>
                <span className="cbo-card__btns">
                  <Phone name={m.holder.name} phone={m.phone} />
                  <Link to={kp.cashHolder(m.holder.userId)} className="kh-btn">
                    {t("Open day")}
                  </Link>
                  {m.kind === "HAND_PAY" ? <MarkReason m={m} date={d.date} onDone={reload} /> : null}
                </span>
              </li>
            ))}
            {d.otherEntries.length ? <OtherEntries entries={d.otherEntries} onDone={reload} owner={owner} /> : null}
          </ul>
        </section>
      ))}

      {speed.length ? (
        <div className="cbo-speed">
          <p>
            <strong>{t("Checking speed (30 days)")}</strong>
          </p>
          {speed.map((s) => (
            <p key={s.verifier.userId}>
              {t("{{name}}: {{sec}} s a row, corrects {{pct}}% · {{rows}} rows", {
                name: s.verifier.name,
                sec: Math.round((s.medianMsPerRow / 1000) * 10) / 10,
                pct: Math.round(Number(s.correctionRate || 0) * 100),
                rows: s.rows,
              })}
            </p>
          ))}
          <p className="cb-muted">{t("Very fast with no corrections can mean rows aren't being looked at.")}</p>
        </div>
      ) : null}
    </>
  );
};

/**
 * The owner's and manager's cash book (V1_SPEC 11.6 L1/I1/M1): what is waiting to be checked, oldest
 * day first, plus the supervisors' cash in hand.
 */
const OwnerInbox = () => {
  const { t } = useKT();
  const { search } = useLocation();
  const tab = new URLSearchParams(search).get("tab") === "supervisors" ? "supervisors" : "inbox";
  return (
    <div className="cb cbo">
      <PageHeader title={t(tab === "supervisors" ? "Supervisors" : "To check")} back={kp.home()} backLabel={t("Back")} />
      <OwnerTabs on={tab} />
      {tab === "supervisors" ? <Supervisors /> : <Inbox />}
    </div>
  );
};

export default OwnerInbox;
